import { ChevronRight } from 'lucide-react';
import { useLabel } from '@/hooks/useLabel';
import { cn } from '@/lib/utils';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  const breadcrumbLabel = useLabel('common.breadcrumb', 'Breadcrumb');
  if (items.length === 0) return null;

  return (
    <nav
      className={cn('flex items-center gap-2 text-sm text-muted-foreground', className)}
      aria-label={breadcrumbLabel}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isClickable = !isLast && (item.href || item.onClick);

        return (
          <div key={index} className="flex items-center gap-2">
            {isClickable ? (
              <button
                type="button"
                onClick={item.onClick}
                className="transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </button>
            ) : (
              <span
                className={cn(isLast && 'font-medium text-foreground')}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            )}
            {!isLast && <ChevronRight className="h-4 w-4" aria-hidden="true" />}
          </div>
        );
      })}
    </nav>
  );
}
